import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import type { PublicCall } from '../../types'
import { callShareText, callUrl, linkedInShareUrl, xShareUrl } from '../../utils/callShareText'

/** Where "Write a longer post" opens: Content's Create tab, from this call. */
export const longerPostPath = (id: number) => `/create?call=${id}`

const canWebShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function'

const BUTTON = 'rounded-md border border-terminal-border px-3 py-1.5 text-sm font-medium text-gray-200 hover:bg-white/5'

/**
 * Share a call from its receipt, for the owner and visitors alike. Every
 * option posts callShareText, unchanged; the owner can also open Content to
 * write a longer post from the same facts.
 */
const ShareMenu = ({ call, isOwner, justLocked }: { call: PublicCall; isOwner: boolean; justLocked: boolean }) => {
  const [copied, setCopied] = useState<'text' | 'link' | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(timer.current), [])

  const text = callShareText(call)
  if (!text) return null
  const url = callUrl(call.id)

  const copy = async (value: string, what: 'text' | 'link') => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(what)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(null), 2000)
      return true
    } catch {
      toast.error(`Could not copy. ${what === 'link' ? `The link is ${url}` : 'Select the text above and copy it.'}`)
      return false
    }
  }

  const webShare = async () => {
    try {
      await navigator.share({ text })
    } catch (error) {
      // Closing the share sheet is not an error.
      if ((error as { name?: string })?.name !== 'AbortError') toast.error('Could not open the share sheet')
    }
  }

  // share-offsite takes only the link, so the text goes on the clipboard to paste.
  const linkedIn = () => {
    void copy(text, 'text').then((ok) => ok && toast.success('Text copied: paste it into your LinkedIn post'))
  }

  return (
    <section
      className="mb-4 rounded-xl border border-terminal-border bg-terminal-surface p-4 text-sm"
      aria-label="Share this call"
      data-testid="share-menu"
    >
      {justLocked && (
        <p className="mb-3 font-semibold text-green-300" data-testid="just-locked">
          Call locked. Share the receipt:
        </p>
      )}
      <pre className="whitespace-pre-wrap break-words rounded bg-terminal-bg p-3 font-sans text-gray-200" data-testid="share-text">
        {text}
      </pre>
      <div className="mt-3 flex flex-wrap gap-2">
        {canWebShare() && (
          <button type="button" onClick={webShare} className={BUTTON} data-testid="share-native">
            Share…
          </button>
        )}
        <a href={xShareUrl(text)} target="_blank" rel="noopener noreferrer" className={BUTTON} data-testid="share-x">
          Post to X
        </a>
        <a
          href={linkedInShareUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={linkedIn}
          className={BUTTON}
          data-testid="share-linkedin"
        >
          Share on LinkedIn
        </a>
        <button type="button" onClick={() => copy(text, 'text')} className={BUTTON} data-testid="copy-text">
          {copied === 'text' ? 'Copied' : 'Copy text'}
        </button>
        <button type="button" onClick={() => copy(url, 'link')} className={BUTTON} data-testid="copy-link">
          {copied === 'link' ? 'Copied' : 'Copy link'}
        </button>
        {isOwner && (
          <Link
            to={longerPostPath(call.id)}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-terminal-bg hover:bg-accent-hover"
            data-testid="write-longer-post"
          >
            Write a longer post
          </Link>
        )}
      </div>
    </section>
  )
}

export default ShareMenu
